import { booleanAttribute, Directive, effect, inject, input, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { PageCursorMetadata } from '@tran-monorepo/interfaces';
import { Application, AwardCommitteeMeeting } from '@tran-monorepo/interfaces/applications';
import { File } from '@tran-monorepo/interfaces/files';

/**
 * Abstract base directive for the application detail page.
 * Provides the shared inputs, store interaction logic, and abstract members
 * that concrete implementations (customer, management) must provide.
 *
 * Usage:
 *   1. Extend this class in your app-specific component.
 *   2. Decorate the subclass with @Component and point to a template.
 *   3. Implement all abstract members using the app-specific NGXS state.
 */
@Directive()
export abstract class BaseApplicationDetailComponent {
    protected readonly store = inject(Store);

    public readonly applicationId = input<string>('');
    public readonly readonly = input(true, { transform: booleanAttribute });

    /** App-specific signal for the loaded application */
    protected abstract readonly application: Signal<Application | null>;
    /** App-specific signal for the loading state of the application */
    protected abstract readonly isLoadingApplication: Signal<boolean>;
    /** App-specific signal for the loaded attachments */
    protected abstract readonly attachments: Signal<File[]>;
    /** App-specific signal for attachments pagination metadata */
    protected abstract readonly attachmentsMetadata: Signal<PageCursorMetadata | null>;
    /** App-specific signal for the selected award committee meeting */
    protected abstract readonly selectedAwardCommitteeMeeting: Signal<AwardCommitteeMeeting | null>;
    /** App-specific signal for the loading state of the selected award committee meeting */
    protected abstract readonly isSelectedAwardCommitteeMeetingLoading: Signal<boolean>;

    constructor() {
        effect(() => {
            const applicationId = this.applicationId();
            if (applicationId) {
                this.loadData(applicationId);
            }
        });
    }

    /**
     * Triggered when applicationId changes.
     * Implementations must dispatch the appropriate NGXS actions to load application data.
     */
    protected abstract loadData(applicationId: string): void;

    protected downloadAttachment(file: File): void {
        this.dispatchDownloadAttachment(file);
    }

    protected loadMoreAttachments(): void {
        this.dispatchLoadMoreAttachments();
    }

    /**
     * Dispatch the download action — override if your action differs.
     */
    protected abstract dispatchDownloadAttachment(file: File): void;

    /**
     * Dispatch the load-more action — override if your action differs.
     */
    protected abstract dispatchLoadMoreAttachments(): void;
}
